import React, { useEffect, useState, useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Bell, Coffee, ListTodo, RotateCcw, X, Timer, Volume2, Play } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { realMinPerGaimHour, gaimOffsetMin, gaimToRealMs, fmtEpochHM } from "@/lib/clock";
import { playAlarm, startAlarmLoop, stopAlarm, getAlarmSound, setAlarmSound, ALARM_SOUNDS } from "@/lib/alarm";
import { useI18n } from "@/lib/i18n";
import { TimeSelect } from "@/components/TimeSelect";

function notify(title, body) {
  try {
    if ("Notification" in window && Notification.permission === "granted" && navigator.serviceWorker) {
      navigator.serviceWorker.ready.then((reg) => reg.showNotification(title, { body, icon: "/icon-192.png", badge: "/icon-192.png" }));
    }
  } catch { /* ignore */ }
}

export default function Planning({ settings, date, tz }) {
  const { t } = useI18n();
  const gain = settings.target_gain;
  const startTime = settings.start_time;
  const [blocks, setBlocks] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ label: "", kind: "task", gaim_start: startTime, gaim_end: "", alarm_start: true, alarm_end: true });
  const firedRef = useRef(new Set());
  const [sound, setSound] = useState(getAlarmSound());

  const offToGaimHM = (off) => {
    const [sh, sm] = startTime.split(":").map(Number);
    const total = (((sh * 60 + sm + off) % 1440) + 1440) % 1440;
    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  };

  const changeSound = (val) => {
    setSound(val);
    setAlarmSound(val);
    playAlarm(val, 1);
  };

  const generatePomodoro = async () => {
    let off = 0;
    const reqs = [];
    for (let i = 0; i < 8; i++) {
      reqs.push({ label: "Focus", kind: "task", gaim_start: offToGaimHM(off), gaim_end: offToGaimHM(off + 25), alarm_start: true, alarm_end: true });
      off += 25;
      reqs.push({ label: t("pv_break"), kind: "break", gaim_start: offToGaimHM(off), gaim_end: offToGaimHM(off + 5), alarm_start: false, alarm_end: true });
      off += 5;
    }
    try {
      await api.post("/planning/blocks/bulk", { date, blocks: reqs });
      await load();
      toast.success(t("pv_pomodoro_done"));
    } catch { toast.error("Error"); }
  };

  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(i);
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/planning/blocks/${date}`);
      setBlocks(data);
    } catch { /* ignore */ }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  // Alarm engine (reliable while the app is open)
  useEffect(() => {
    blocks.forEach((b) => {
      [["start", b.gaim_start, b.alarm_start], ["end", b.gaim_end, b.alarm_end]].forEach(([edge, gaimHM, on]) => {
        if (!on) return;
        const key = `${date}:${b.id}:${edge}`;
        if (firedRef.current.has(key)) return;
        const realMs = gaimToRealMs(gaimHM, startTime, gain, tz);
        if (now >= realMs && now - realMs < 90000) {
          firedRef.current.add(key);
          startAlarmLoop(getAlarmSound());
          const msg = `${b.label || (b.kind === "break" ? t("pv_break") : t("pv_task"))} · ${gaimHM}`;
          toast(`🔔 ${t("pv_fired")} — ${msg}`, {
            duration: Infinity,
            action: { label: t("pv_stop"), onClick: () => stopAlarm() },
            onDismiss: () => stopAlarm(),
          });
          notify(`🔔 ${t("pv_fired")}`, msg);
        }
      });
    });
  }, [now, blocks, startTime, gain, tz, date, t]);

  const addBlock = async () => {
    const so = gaimOffsetMin(form.gaim_start, startTime);
    const eo = gaimOffsetMin(form.gaim_end || "", startTime);
    if (!form.gaim_end || eo <= so) { toast.error(t("pv_invalid")); return; }
    try {
      await api.post("/planning/blocks", { ...form, date });
      setForm({ label: "", kind: "task", gaim_start: startTime, gaim_end: "", alarm_start: true, alarm_end: true });
      setFormOpen(false);
      load();
    } catch { toast.error("Error"); }
  };

  const removeBlock = async (id) => {
    try { await api.delete(`/planning/blocks/${id}`); load(); }
    catch { toast.error("Error"); }
  };

  const resetAll = async () => {
    try { await api.delete(`/planning/blocks/reset/${date}`); setBlocks([]); firedRef.current = new Set(); toast.success(t("reset")); }
    catch { toast.error("Error"); }
  };

  const sorted = [...blocks].sort((a, b) => gaimOffsetMin(a.gaim_start, startTime) - gaimOffsetMin(b.gaim_start, startTime));

  // Build a timeline: blocks + "free" gaps between them.
  const segments = [];
  let cursor = 0;
  sorted.forEach((b) => {
    const so = gaimOffsetMin(b.gaim_start, startTime);
    const eo = gaimOffsetMin(b.gaim_end, startTime);
    if (so > cursor) segments.push({ free: true, startOff: cursor, endOff: so });
    segments.push({ free: false, block: b, startOff: so, endOff: eo });
    cursor = Math.max(cursor, eo);
  });
  if (sorted.length && cursor < 1440) segments.push({ free: true, startOff: cursor, endOff: 1440 });

  const realRange = (b) => `${fmtEpochHM(gaimToRealMs(b.gaim_start, startTime, gain, tz), tz)}-${fmtEpochHM(gaimToRealMs(b.gaim_end, startTime, gain, tz), tz)}`;

  return (
    <div className="gt-card p-5 md:p-6" data-testid="planning-v2">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-display font-bold text-lg">{t("planning_t")}</h3>
          <p className="text-sm text-[#00E676] font-mono-clock mt-1">{t("conv", { n: realMinPerGaimHour(gain) })}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 rounded-[10px] border border-white/10 bg-[#0A0A0A] px-2 h-9">
            <Volume2 size={14} className="text-white/40" />
            <select data-testid="pv-sound-select" value={sound} onChange={(e) => changeSound(e.target.value)}
              className="bg-transparent text-white text-xs outline-none cursor-pointer">
              {ALARM_SOUNDS.map((s) => <option key={s} value={s} className="bg-[#111]">{t(`snd_${s}`)}</option>)}
            </select>
            <button data-testid="pv-sound-test" onClick={() => playAlarm(sound, 1)} className="text-[#00E676] hover:scale-110 transition-transform" title={t("pv_test")}>
              <Play size={13} />
            </button>
          </div>
          <Button data-testid="planning-pomodoro-btn" onClick={generatePomodoro} variant="outline"
            className="border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10 rounded-[12px] h-9 text-sm font-semibold">
            <Timer size={15} className="mr-1.5" /> {t("pv_pomodoro")}
          </Button>
          <Button data-testid="planning-add-btn" onClick={() => setFormOpen((v) => !v)}
            className="bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] h-9 text-sm font-semibold">
            <Plus size={16} className="mr-1.5" /> {t("pv_add")}
          </Button>
          {blocks.length > 0 && (
            <Button data-testid="planning-reset-btn" onClick={resetAll} variant="outline"
              className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-[12px] h-9">
              <RotateCcw size={15} />
            </Button>
          )}
        </div>
      </div>

      {formOpen && (
        <div className="rounded-[14px] border border-white/10 bg-[#0A0A0A] p-4 mb-4 space-y-3" data-testid="planning-form">
          <Input data-testid="pv-label-input" value={form.label} placeholder={t("pv_label")}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            className="bg-[#111] border-white/10 rounded-[10px] h-10 text-sm" />
          <div className="flex gap-2">
            <button data-testid="pv-type-task" onClick={() => setForm((f) => ({ ...f, kind: "task" }))}
              className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-[10px] text-sm border transition-colors ${form.kind === "task" ? "border-[#007BFF] bg-[#007BFF]/10 text-[#007BFF]" : "border-white/10 text-white/50"}`}>
              <ListTodo size={16} /> {t("pv_task")}
            </button>
            <button data-testid="pv-type-break" onClick={() => setForm((f) => ({ ...f, kind: "break" }))}
              className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-[10px] text-sm border transition-colors ${form.kind === "break" ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676]" : "border-white/10 text-white/50"}`}>
              <Coffee size={16} /> {t("pv_break")}
            </button>
          </div>
          <div className="flex gap-3">
            <label className="flex-1 text-xs text-white/50">
              {t("pv_start")}
              <TimeSelect testId="pv-start" value={form.gaim_start}
                onChange={(v) => setForm((f) => ({ ...f, gaim_start: v }))} />
            </label>
            <label className="flex-1 text-xs text-white/50">
              {t("pv_end")}
              <TimeSelect testId="pv-end" value={form.gaim_end}
                onChange={(v) => setForm((f) => ({ ...f, gaim_end: v }))} />
            </label>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-white/70 flex items-center gap-2"><Bell size={15} /> {t("pv_alarm_start")}</span>
            <Switch data-testid="pv-alarm-start" checked={form.alarm_start} onCheckedChange={(v) => setForm((f) => ({ ...f, alarm_start: v }))} className="data-[state=checked]:bg-[#007BFF]" />
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-white/70 flex items-center gap-2"><Bell size={15} /> {t("pv_alarm_end")}</span>
            <Switch data-testid="pv-alarm-end" checked={form.alarm_end} onCheckedChange={(v) => setForm((f) => ({ ...f, alarm_end: v }))} className="data-[state=checked]:bg-[#007BFF]" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button data-testid="pv-save-btn" onClick={addBlock} className="flex-1 bg-[#00E676] hover:bg-[#00c765] text-black rounded-[10px] h-10 font-semibold">{t("pv_save")}</Button>
            <Button data-testid="pv-cancel-btn" onClick={() => setFormOpen(false)} variant="ghost" className="text-white/50 rounded-[10px] h-10"><X size={16} /></Button>
          </div>
        </div>
      )}

      {segments.length === 0 ? (
        <p className="text-white/40 text-sm py-6 text-center" data-testid="planning-empty">{t("pv_none")}</p>
      ) : (
        <div className="space-y-2">
          {segments.map((seg, idx) => {
            if (seg.free) {
              return (
                <div key={`free-${idx}`} data-testid="planning-free" className="flex items-center gap-3 rounded-[12px] px-3 py-2 border border-dashed border-white/10 bg-transparent">
                  <div className="w-28 shrink-0 font-mono-clock text-xs text-white/30">{offToGaimHM(seg.startOff)} - {offToGaimHM(seg.endOff)}</div>
                  <span className="text-xs text-white/25">{t("pv_free")}</span>
                </div>
              );
            }
            const b = seg.block;
            const rs = gaimToRealMs(b.gaim_start, startTime, gain, tz);
            const re = gaimToRealMs(b.gaim_end, startTime, gain, tz);
            const isCurrent = now >= rs && now < re;
            const isBreak = b.kind === "break";
            const accent = isBreak ? "#00E676" : "#007BFF";
            return (
              <div key={b.id} data-testid={`planning-block-${b.id}`}
                className="flex items-center gap-3 rounded-[12px] px-3 py-2.5 border transition-all"
                style={{ borderColor: isCurrent ? accent : "rgba(255,255,255,0.08)", background: isCurrent ? `${accent}10` : "#0A0A0A" }}>
                <div className="w-28 shrink-0">
                  <div className="font-mono-clock text-sm font-semibold" style={{ color: accent }}>{b.gaim_start} - {b.gaim_end}</div>
                  <div className="font-mono-clock text-[10px] text-white/30 mt-0.5">{t("real")} {realRange(b)}</div>
                </div>
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  {isBreak ? <Coffee size={15} style={{ color: accent }} className="shrink-0" /> : <ListTodo size={15} style={{ color: accent }} className="shrink-0" />}
                  <span className="text-sm text-white/90 truncate">{b.label || (isBreak ? t("pv_break") : t("pv_task"))}</span>
                  {(b.alarm_start || b.alarm_end) && <Bell size={13} className="text-white/40 shrink-0" />}
                </div>
                {isCurrent && <span className="text-[10px] shrink-0 gt-live-dot" style={{ color: accent }}>● {t("now")}</span>}
                <button data-testid={`planning-del-${b.id}`} onClick={() => removeBlock(b.id)} className="text-white/30 hover:text-[#FF3B30] transition-colors shrink-0">
                  <Trash2 size={15} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
